// Career Map — shared head() builder for the section routes.
// Produces title/description/OG + canonical + hreflang alternates, and
// loads the Google fonts + section stylesheet the static pages use.

import careersCss from "./careers.css?url";
import { SITE_CHROME_LINKS } from "@/components/site/site-chrome-head";
import type { Lang } from "./spheres";

const BASE = "https://yolutap.com";
export const BASE_URL = BASE;
const OG_LOCALE: Record<Lang, string> = { az: "az_AZ", ru: "ru_RU", en: "en_US" };
// Branded social image (same asset the static marketing pages use). Without
// this, the careers/sphere pages inherit the root's placeholder og:image.
const OG_IMAGE = `${BASE}/photos/teen_boy_hero.jpg`;

export type CareersHeadArgs = {
  lang: Lang;
  /** Path portion for the current locale, e.g. "/careers" or "/az/careers/healthcare". */
  canonicalPath: string;
  /** Same page in all three locales (paths), for hreflang. */
  altPaths: Record<Lang, string>;
  title: string;
  description: string;
  /** Optional JSON-LD objects (e.g. BreadcrumbList, Occupation) → ld+json scripts. */
  jsonLd?: Record<string, unknown>[];
};

export function careersHead({ lang, canonicalPath, altPaths, title, description, jsonLd }: CareersHeadArgs) {
  const canonical = BASE + canonicalPath;
  return {
    scripts: (jsonLd ?? []).map((obj) => ({
      type: "application/ld+json",
      children: JSON.stringify(obj),
    })),
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical },
      { property: "og:locale", content: OG_LOCALE[lang] },
      // og:locale:alternate intentionally omitted here: TanStack's head manager
      // dedupes <meta> by `name ?? property`, so only one alternate would ever
      // render (misleading — an AZ page would advertise en but not ru). The
      // complete, canonical locale signal is the hreflang link set below, which
      // renders in full. Legal/landing pages are static HTML and keep both.
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: canonical },
      { rel: "alternate", hrefLang: "az", href: BASE + altPaths.az },
      { rel: "alternate", hrefLang: "ru", href: BASE + altPaths.ru },
      { rel: "alternate", hrefLang: "en", href: BASE + altPaths.en },
      // x-default — азербайджанская версия (27.10.2026, решение владельца: x-default везде = /az).
      { rel: "alternate", hrefLang: "x-default", href: BASE + altPaths.az },
      { rel: "stylesheet", href: careersCss },
      ...SITE_CHROME_LINKS,
    ],
  };
}
